import { PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import { usePermissions } from '../auth/use-permissions'
import { RivenaLockup, RivenaSymbol } from '../components/brand/rivena-mark'
import { cn } from '../lib/cn'
import { activeNavPath, filterNavItems, groupNavItems } from './nav-items'

export interface SidebarProps {
  collapsed: boolean
  onToggleCollapsed?: () => void
  /** Closes the mobile drawer once a destination is picked. */
  onNavigate?: () => void
  className?: string
}

export function Sidebar({ collapsed, onToggleCollapsed, onNavigate, className }: SidebarProps) {
  const { t } = useTranslation()
  const { roles } = usePermissions()

  // Filtering happens against the same matrix the API enforces. It hides what the
  // user cannot use; it does not protect anything — the routes re-check, and the
  // server re-checks again on every call.
  const groups = useMemo(() => groupNavItems(filterNavItems(roles)), [roles])
  const { pathname } = useLocation()
  const activePath = activeNavPath(
    pathname,
    groups.flatMap((group) => group.items),
  )

  return (
    <aside
      className={cn(
        'flex h-full flex-col border-e border-border bg-surface transition-[width] duration-200',
        collapsed ? 'w-14' : 'w-60',
        className,
      )}
    >
      <div
        className={cn(
          'flex h-14 shrink-0 items-center gap-2 border-b border-border px-3',
          collapsed && 'justify-center px-0',
        )}
      >
        {collapsed ? (
          <RivenaSymbol className="h-7 w-7 shrink-0 rounded" />
        ) : (
          // The lockup replaces the icon AND the app name — it contains the wordmark,
          // so keeping the text beside it would print "RIVENA" twice.
          <RivenaLockup className="h-5 shrink-0" />
        )}
      </div>

      <nav
        aria-label={t('common:sidebar.navigation')}
        className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-3"
      >
        {groups.map((group) => (
          <div key={group.section} className="mb-4 last:mb-0">
            {!collapsed ? (
              <p className="px-2 pb-1.5 text-xs font-medium uppercase tracking-wider text-foreground-subtle">
                {t(`nav:sections.${group.section}`)}
              </p>
            ) : null}

            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon
                const label = t(`nav:items.${item.key}`)
                const isActive = item.path === activePath
                return (
                  <li key={item.key}>
                    <Link
                      to={item.path}
                      onClick={onNavigate}
                      aria-current={isActive ? 'page' : undefined}
                      title={collapsed ? label : t(`nav:hints.${item.key}`)}
                      className={cn(
                        'flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors',
                        collapsed && 'justify-center px-0',
                        isActive
                          ? 'bg-accent-subtle font-medium text-accent-text'
                          : 'text-foreground-muted hover:bg-surface-hover hover:text-foreground',
                      )}
                    >
                      <Icon aria-hidden className="h-4 w-4 shrink-0" />
                      {collapsed ? <span className="sr-only">{label}</span> : <span>{label}</span>}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </nav>

      {onToggleCollapsed ? (
        <div className="shrink-0 border-t border-border p-2">
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-label={collapsed ? t('common:sidebar.expand') : t('common:sidebar.collapse')}
            className={cn(
              'flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-foreground-muted',
              'transition-colors hover:bg-surface-hover hover:text-foreground',
              collapsed && 'justify-center px-0',
            )}
          >
            {collapsed ? (
              <PanelLeftOpen aria-hidden className="h-4 w-4" />
            ) : (
              <>
                <PanelLeftClose aria-hidden className="h-4 w-4" />
                <span>{t('common:sidebar.collapse')}</span>
              </>
            )}
          </button>
        </div>
      ) : null}
    </aside>
  )
}
